import { useEffect, useMemo, useReducer, useRef } from 'react';
import { getAllCards } from '../../tarot-data/cards';
import { shuffleCards } from '../../tarot-utils/cardUtils';

// The steps of a reading, in the order they stack down the page. Each one
// stays on screen once passed, locked, so the reader can scroll back
// through how the reading came about.
export const PHASES = ['shuffle', 'pick', 'reveal', 'reading'];

// How long the deck's shuffle animation runs. DeckSection hands this to its
// stylesheet as --shuffle-dur, so the animation and the flow share one clock.
export const SHUFFLE_MS = 1200;

// The fan's two moves (CardFanSelector): rolling its cards round the circle,
// and folding the selector down round the pile, or back up
export const FAN_ROLL_MS = 1000;
export const FAN_FOLD_MS = 700;

// With motion reduced there is no animation to wait for
const shuffleDelay = () =>
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 0 : SHUFFLE_MS;

const freshDeck = () => shuffleCards(getAllCards());

/**
 * `deck` is the deck as it stands on the table: the cards not yet taken, in
 * their shuffled order. `drawn` is the cards taken from it, in the order
 * they were picked. Between them they always hold every card exactly once.
 */
export const initialFlow = (numCards, deck = freshDeck()) => ({
  phase: 'shuffle',
  numCards,
  deck,
  isShuffling: false,
  hasShuffled: false,
  drawn: [],
  revealed: []
});

/**
 * Every action is only honoured in the phase that owns it, so a step the
 * flow has moved past cannot change what came after it.
 */
export function flowReducer(state, action) {
  switch (action.type) {
    case 'shuffle':
      if (state.phase !== 'shuffle') return state;
      return { ...state, deck: action.deck, isShuffling: true };
    case 'shuffled':
      return { ...state, isShuffling: false, hasShuffled: true };
    case 'draw':
      if (state.phase !== 'shuffle' || state.isShuffling) return state;
      return { ...state, phase: 'pick' };
    case 'pick': {
      // One card at a time, out of the deck and straight into its place in
      // the spread; the last one opens the cards to turning
      if (state.phase !== 'pick' || state.drawn.length >= state.numCards) return state;
      if (!state.deck.includes(action.card)) return state;
      const deck = state.deck.filter(card => card !== action.card);
      const drawn = [...state.drawn, action.card];
      return { ...state, deck, drawn, phase: drawn.length === state.numCards ? 'reveal' : 'pick' };
    }
    case 'reveal':
      if (state.phase !== 'reveal' || state.revealed.includes(action.index)) return state;
      return { ...state, revealed: [...state.revealed, action.index] };
    case 'revealAll':
      if (state.phase !== 'reveal') return state;
      return { ...state, revealed: state.drawn.map((_, index) => index) };
    case 'requestReading':
      if (state.phase !== 'reveal' || state.revealed.length !== state.drawn.length) return state;
      return { ...state, phase: 'reading' };
    default:
      return state;
  }
}

/** Whether the flow has got as far as `phase`. */
export const hasReached = (current, phase) => PHASES.indexOf(current) >= PHASES.indexOf(phase);

/** Whether the flow has moved on from `phase`, which is then locked. */
export const hasPassed = (current, phase) => PHASES.indexOf(current) > PHASES.indexOf(phase);

/** One reading of a spread of `numCards`, from shuffling the deck to the AI reading. */
export function useReadingFlow(numCards) {
  const [state, dispatch] = useReducer(flowReducer, numCards, initialFlow);
  const shuffleTimer = useRef(null);
  const deckNow = useRef(state.deck);
  deckNow.current = state.deck;

  useEffect(() => () => clearTimeout(shuffleTimer.current), []);

  const actions = useMemo(() => ({
    shuffle: () => {
      // Shuffled out here, not in the reducer, so the reducer stays pure.
      // It is the deck as it stands that is shuffled: cards already taken
      // stay out of it.
      dispatch({ type: 'shuffle', deck: shuffleCards(deckNow.current) });
      clearTimeout(shuffleTimer.current);
      shuffleTimer.current = setTimeout(() => dispatch({ type: 'shuffled' }), shuffleDelay());
    },
    draw: () => dispatch({ type: 'draw' }),
    pick: (card) => dispatch({ type: 'pick', card }),
    reveal: (index) => dispatch({ type: 'reveal', index }),
    revealAll: () => dispatch({ type: 'revealAll' }),
    requestReading: () => dispatch({ type: 'requestReading' })
  }), []);

  const allRevealed = state.drawn.length > 0 && state.revealed.length === state.drawn.length;

  return { ...state, ...actions, allRevealed };
}
